/* import __COLOCATED_TEMPLATE__ from './escalation-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type EscalationRule from 'embercom/models/ai-agent/escalation-rule';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { states } from 'embercom/models/data/matching-system/matching-constants';
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { type GuidelineCategory } from 'embercom/models/ai-agent/guidelines';
import { Stat } from 'embercom/components/ai-agent/guidance/collapsible-list';
import { EntityType } from 'embercom/models/data/entity-types';
import {
  type SaveCallBackFunction as AudienceSaveCallBackFunction,
  type AiAudienceEntity,
} from 'embercom/components/knowledge-hub/content-editor/shared/audience/dropdown';

export interface Args {
  title: string;
  category: GuidelineCategory;
  description: string;
  escalationRules: EscalationRule[];
  icon: InterfaceIconName;
  maxGuidelineCount: number;
  chartDefinitionsHash: Record<string, Record<Stat, any>>;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class EscalationSection extends Component<Signature> {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare intercomEventService: $TSFixMe;

  @tracked isOpen = true;
  @tracked sideDrawerOpen = false;
  @tracked selectedEscalationRule?: EscalationRule;
  @tracked selectedStat?: Stat;
  @tracked openSectionId: string | null = null;

  // Map to store audience save callbacks for each escalation rule
  private audienceSaveCallbacks = new Map<string, AudienceSaveCallBackFunction>();

  // Map to track local aiContentSegmentIds for each escalation rule
  // This helps detect unsaved audience changes per rule
  private localAiContentSegmentIds = new Map<string, number[]>();

  stats = [Stat.USED, Stat.RESOLVED, Stat.ROUTED];

  drillInColumns = [
    'conversation.id',
    'conversation.first_user_conversation_part_created_at',
    'conversation.fin.resolution_state',
    'conversation.channel_type',
    'conversation.bot_conversation_rating.rating_index',
    'conversation.fin.escalation_rules_applied.escalation_rule_ids',
  ];

  get app(): any {
    return this.appService.app;
  }

  get states(): typeof states {
    return states;
  }

  get escalationRules(): EscalationRule[] {
    return this.args.escalationRules.toArray();
  }

  get arrowIconClass(): InterfaceIconName {
    return this.isOpen ? 'small-thin-down-arrow' : 'small-thin-right-arrow';
  }

  get maybeEmptyStateClasses(): string {
    return this.escalationRules.length ? '' : 'filter grayscale opacity-50';
  }

  get headerOpenClassName(): string {
    return 'collapsible-list__header__open net-new-flag';
  }

  get escalationRuleCount(): number {
    return this.escalationRules.length;
  }

  get cannotAddNewEscalationRules(): boolean {
    return this.escalationRuleCount >= this.args.maxGuidelineCount || this.hasUnsavedEscalationRule;
  }

  get hasUnsavedEscalationRule(): boolean {
    return this.escalationRules.some(
      (escalationRule) =>
        escalationRule.state === states.draft &&
        (escalationRule.hasDirtyAttributes || escalationRule.name === ''),
    );
  }

  get canAdminAccessReporting(): boolean {
    return this.permissionsService.currentAdminCan('can_access_reporting');
  }

  get canUseEscalationRuleAudience(): boolean {
    return this.appService.app.canUseFeature('audience-for-escalation-rule');
  }

  getAiAudienceEntity = (escalationRule: EscalationRule): AiAudienceEntity => {
    return {
      aiContentSegmentIds: escalationRule.aiContentSegmentIds,
      entityId: Number(escalationRule.id),
      entityType: EntityType.EscalationRule,
    };
  };

  hasUnsavedAudienceChanges = (escalationRule: EscalationRule): boolean => {
    if (!this.canUseEscalationRuleAudience) {
      return false;
    }

    let localSegmentIds = this.localAiContentSegmentIds.get(escalationRule.id);
    if (!localSegmentIds) {
      return false;
    }

    // Compare arrays - if they differ, there are unsaved audience changes
    if (escalationRule.aiContentSegmentIds.length !== localSegmentIds.length) {
      return true;
    }

    return !escalationRule.aiContentSegmentIds.every((id) => localSegmentIds.includes(id));
  };

  private getAudienceSaveCallbackParams(
    escalationRule: EscalationRule,
  ): Parameters<AudienceSaveCallBackFunction> {
    let localSegmentIds = this.localAiContentSegmentIds.get(escalationRule.id) || [];
    let segmentIdsToAdd = escalationRule.aiContentSegmentIds.filter(
      (id) => !localSegmentIds.includes(id),
    );
    let segmentIdsToDelete = localSegmentIds.filter(
      (id) => !escalationRule.aiContentSegmentIds.includes(id),
    );
    // isAudienceReset should only be true when:
    // 1. There were previously saved segments
    // 2. AND all of them are being deleted
    // 3. AND no new segments are being added
    let isAudienceReset =
      localSegmentIds.length > 0 &&
      segmentIdsToDelete.length === localSegmentIds.length &&
      escalationRule.aiContentSegmentIds.length === 0;
    return [segmentIdsToAdd, segmentIdsToDelete, isAudienceReset];
  }

  private initializeLocalAudienceTracking(escalationRule: EscalationRule): void {
    if (!this.localAiContentSegmentIds.has(escalationRule.id)) {
      this.localAiContentSegmentIds.set(
        escalationRule.id,
        escalationRule.aiContentSegmentIds.slice(),
      );
    }
  }

  private async saveEscalationRuleWithAudience(escalationRule: EscalationRule): Promise<void> {
    // Save audience changes first, before saving the escalation rule
    // If we save the escalation rule first, the backend may reset the audience with stale data
    if (this.canUseEscalationRuleAudience) {
      let audienceCallback = this.audienceSaveCallbacks.get(escalationRule.id);
      if (audienceCallback) {
        let audienceParams = this.getAudienceSaveCallbackParams(escalationRule);
        await audienceCallback(...audienceParams);
        // Update local tracking after successful save
        this.localAiContentSegmentIds.set(
          escalationRule.id,
          escalationRule.aiContentSegmentIds.slice(),
        );
      }
    }

    await escalationRule.save();
  }

  @action
  openSideDrawer(escalationRule: EscalationRule, stat: Stat, event: Event) {
    this.trackEvent(stat);
    event.stopPropagation();
    event.preventDefault();
    if (this.permissionsService.currentAdminCan('can_reporting__drillin__access')) {
      this.selectedEscalationRule = escalationRule;
      this.selectedStat = stat;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  closeSideDrawer() {
    this.selectedEscalationRule = undefined;
    this.selectedStat = undefined;
  }

  @action
  trackEvent(stat_clicked: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'guidance_stat',
      place: 'guidance_card',
      section: 'guidance',
      stat: stat_clicked,
    });
  }

  @action
  registerAudienceSaveCallback(
    escalationRule: EscalationRule,
    callback: AudienceSaveCallBackFunction,
  ): void {
    this.audienceSaveCallbacks.set(escalationRule.id, callback);
    // Initialize local tracking when callback is registered
    this.initializeLocalAudienceTracking(escalationRule);
  }

  @action
  audienceSegmentChanged(): void {
    this.aiAgentGuidelineService.updatePreview();
  }

  @action
  toggleOpenState(): void {
    this.isOpen = !this.isOpen;
  }

  @action
  onOpenSectionChange(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  async createNewEscalationRule(): Promise<void> {
    let predicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: [],
    });

    let escalationRule = this.store.createRecord('ai-agent/escalation-rule', {
      name: this.intl.t('ai-agent.playground.answer-uses.escalation-rule'),
      state: states.draft,
      appId: this.app.id,
      predicateGroup,
    });

    try {
      await escalationRule.save();
      // Clear the name after save so UI shows placeholder "Enter a title"
      escalationRule.name = '';
    } catch (error) {
      console.error('Failed to create escalation rule:', error);
      return;
    }

    this.isOpen = true;
    this.openSectionId = `escalation:${escalationRule.id || ''}`;
  }

  @action
  async saveEscalationRule(escalationRule: EscalationRule) {
    try {
      await this.saveEscalationRuleWithAudience(escalationRule);
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (error) {
      console.error('Failed to save escalation rule:', error);
    } finally {
      this.aiAgentGuidelineService.updatePreview();
    }
  }

  @action
  async deleteEscalationRule(escalationRule: EscalationRule) {
    try {
      await escalationRule.destroyRecord();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.delete-rule-success-confirmation'),
      );
    } catch (error) {
      console.error('Failed to delete escalation rule:', error);
    } finally {
      this.aiAgentGuidelineService.updatePreview();
    }
  }

  @action
  async disableEscalationRule(escalationRule: EscalationRule) {
    escalationRule.state = states.draft;
    try {
      await escalationRule.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (error) {
      console.error('Failed to disable escalation rule:', error);
    } finally {
      this.aiAgentGuidelineService.updatePreview();
    }
  }

  @action
  async cancelEscalationRule(escalationRule: EscalationRule) {
    let localSegmentIds = this.localAiContentSegmentIds.get(escalationRule.id);
    if (localSegmentIds) {
      escalationRule.aiContentSegmentIds = localSegmentIds.slice();
    }

    escalationRule.rollbackAttributes();

    // If it's a saved escalation rule that is now empty (was just created or rolled back to empty),
    // delete it from the backend
    if (!escalationRule.isNew && !escalationRule.hasPredicates) {
      try {
        await escalationRule.destroyRecord();
      } catch (error) {
        console.error('Failed to delete empty guideline:', error);
      } finally {
        this.aiAgentGuidelineService.updatePreview();
      }
    }
  }

  @action
  async enableEscalationRule(escalationRule: EscalationRule) {
    try {
      escalationRule.state = states.live;
      await this.saveEscalationRuleWithAudience(escalationRule);
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (error) {
      console.error('Failed to enable escalation rule:', error);
      this.notificationsService.notifyResponseError(error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::EscalationSection': typeof EscalationSection;
    'ai-agent/guidance/escalation-section': typeof EscalationSection;
  }
}
